import ListWrapper from "../utils/ListWrapper";
import D3EFeature from "./D3EFeature";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";
import CollectionUtils from "../utils/CollectionUtils";

export default class D3EFeatureBundle extends DBObject {
  private static readonly _FEATURES: number = 0;
  private static readonly _TITLE: number = 1;
  public id: number = 0;
  public otherMaster: DBObject;
  private _title: string = "";
  private _features: Array<D3EFeature> = ListWrapper.inverse(
    this,
    "features",
    D3EFeatureBundle._FEATURES
  );
  public constructor(
    d3eParams?: Partial<{ features: Array<D3EFeature>; title: string }>
  ) {
    super();

    this.setFeatures(
      d3eParams?.features ??
        ListWrapper.inverse(this, "features", D3EFeatureBundle._FEATURES)
    );

    this.setTitle(d3eParams?.title ?? "");
  }
  public get d3eType(): string {
    return "D3EFeatureBundle";
  }
  public static updateFeaturesInverse(on: any, obj: any, type: string): void {
    if ("Insert" === type) {
      on.addToFeatures(obj as D3EFeature);
    }

    if ("Delete" === type) {
      on.removeFromFeatures(obj as D3EFeature);
    }
  }
  public clear(): void {
    this.d3eChanges.clear();
  }
  public get title(): string {
    return this._title;
  }
  public setTitle(val: string): void {
    let isValChanged: boolean = this._title !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(D3EFeatureBundle._TITLE, this._title);

    this._title = val;

    this.fire("title", this);
  }
  public get features(): Array<D3EFeature> {
    return this._features;
  }
  public setFeatures(val: Array<D3EFeature>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this._features,
      val
    );

    if (!isValChanged) {
      return;
    }

    if (!this.d3eChanges.contains(D3EFeatureBundle._FEATURES)) {
      let _old: Array<D3EFeature> = Array.from(this._features);

      this.updateD3EChanges(D3EFeatureBundle._FEATURES, _old);
    }

    this.updateObservableColl("features", this._features, val);

    this._features.clear();

    this._features.addAll(val);

    this.fire("features", this);
  }
  public addToFeatures(val: D3EFeature, index: number = -1): void {
    let _old: Array<D3EFeature> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      D3EFeatureBundle._FEATURES
    );

    if (_isNewChange) {
      _old = Array.from(this._features);
    }

    if (index === -1) {
      if (!this._features.contains(val)) this._features.add(val);
    } else {
      this._features.remove(this._features.elementAt(index));

      this._features.add(val);
    }

    this.fire("features", this, val, true);

    this.updateObservable("features", null, val);

    if (_isNewChange) {
      this.updateD3EChanges(D3EFeatureBundle._FEATURES, _old);
    }
  }
  public removeFromFeatures(val: D3EFeature): void {
    let _old: Array<D3EFeature> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      D3EFeatureBundle._FEATURES
    );

    if (_isNewChange) {
      _old = Array.from(this._features);
    }

    this._features.remove(val);

    this.fire("features", this, val, false);

    if (_isNewChange) {
      this.updateD3EChanges(D3EFeatureBundle._FEATURES, _old);
    }
  }
  public get(field: number): any {
    switch (field) {
      case D3EFeatureBundle._TITLE: {
        return this._title;
      }

      case D3EFeatureBundle._FEATURES: {
        return this._features;
      }
      default: {
        return null;
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): D3EFeatureBundle {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {}
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    let obj: D3EFeatureBundle = dbObj as D3EFeatureBundle;

    obj.id = this.id;

    obj.setTitle(this._title);

    obj.setFeatures(this._features);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case D3EFeatureBundle._TITLE: {
        this.setTitle(value as string);
        break;
      }

      case D3EFeatureBundle._FEATURES: {
        this.setFeatures((value as Array<any>).cast<D3EFeature>().toList());
        break;
      }
    }
  }
}
